// Recruiting ACTION forms — the enums the write endpoints accept that
// lib/recruitingApi.ts does not already export (stages live there —
// RECRUITING_STAGES/STAGE_LABEL — and are reused from here, never redeclared).
// One copy, shared by CandidateActions.tsx and AddCandidateForm.tsx, for the
// same reason locations.ts is the one place clinic order may be decided: a
// second list of the same values is how one of them drifts.
import type { Role } from '../../../lib/nav';
import type { LocationCode } from '../../../lib/locations';

/** Who is looking at the card — passed down from wherever the board mounts
 * CandidateActions (People.tsx eventually reads it off `session.value.me`;
 * this tree never reads the session itself, so it stays testable off plain
 * props, same discipline as every other component here). */
export interface RecruitingViewer {
  role: Role;
  locationCode: LocationCode | null;
  email: string;
}

export const POSITIONS = ['LMT', 'EST', 'FRONT_DESK', 'MANAGER'] as const;
export type Position = (typeof POSITIONS)[number];
export const POSITION_LABEL: Record<Position, string> = {
  LMT: 'LMT',
  EST: 'EST',
  FRONT_DESK: 'Front Desk',
  MANAGER: 'Manager',
};

/**
 * The source values a person may actively CHOOSE, on Add or on Edit.
 *
 * JUDGEMENT CALL: the build brief lists "Sources: INDEED, ME_JOBS, WALK_IN,
 * REHIRE, REFERRAL, OTHER (plus the migrated ones, read-only)" — six values —
 * but then asks AddCandidateForm for "source (the five live sources)". Rather
 * than guess which one of the six the brief meant to drop, all six are kept
 * selectable: OTHER is an explicit enum value distinct from "a migrated
 * legacy source", so excluding it would need its own justification this build
 * has no basis for. Flagged in the handoff report, not decided silently.
 */
export const SOURCES = ['INDEED', 'ME_JOBS', 'WALK_IN', 'REHIRE', 'REFERRAL', 'OTHER'] as const;
export type Source = (typeof SOURCES)[number];
export const SOURCE_LABEL: Record<Source, string> = {
  INDEED: 'Indeed',
  ME_JOBS: 'ME Jobs',
  WALK_IN: 'Walk in',
  REHIRE: 'Rehire',
  REFERRAL: 'Referral',
  OTHER: 'Other',
};

export const OUTCOMES = [
  'NOT_ELIGIBLE', 'NOT_YET_EXPERIENCE', 'REVISIT', 'DECLINED_OFFER', 'OLD_EMPLOYEE', 'CURRENT_EMPLOYEE', 'ARCHIVE',
] as const;
export type Outcome = (typeof OUTCOMES)[number];
export const OUTCOME_LABEL: Record<Outcome, string> = {
  NOT_ELIGIBLE: 'Not eligible',
  NOT_YET_EXPERIENCE: 'Not yet – experience',
  REVISIT: 'Revisit',
  DECLINED_OFFER: 'Declined offer',
  OLD_EMPLOYEE: 'Old employee',
  CURRENT_EMPLOYEE: 'Current employee',
  ARCHIVE: 'Archive',
};
